import { devWarn } from "../../utils/devWarning";
import {
  clampMediaTimelineViewportStart,
  clampMediaTimelineViewportWidth,
  MEDIA_TIMELINE_POLICY,
  resolveMediaTimelineFollowStart,
  resolveMediaTimelineOpeningStart,
  resolveMediaTimelineViewportBounds,
  resolveMediaTimelineViewportView,
  resolveMediaTimelineZoomedWidth,
  type MediaTimelineViewportBounds,
} from "./mediaTimelineMath";
import type {
  MediaTimelineViewportBaseline,
  MediaTimelineViewportControllerOptions,
  MediaTimelineViewportSnapshot,
} from "./timelineTypes";

/**
 * @internal bar 자신의 viewport 입력. 마운트된 bar만 하나를 갖는다 — 특정 시각을 드러내는 길은 seek라서
 * 창을 옮기는 명령은 공개 controller에 두지 않는다. 다른 bar가 controller를 넘겨받으면 모든 메서드가
 * no-op이 된다.
 */
export interface MediaTimelineViewportSession {
  /** bar가 보여 주는 미디어. 새 `source`는 창을 새로 열고, 같은 source의 새 duration은 지금 창을 drift시킨다. */
  syncMedia(media: { source: object; durationSeconds: number; currentTimeSeconds: number }): void;
  /** 그려진 창의 한 지점(0 = 시작, 1 = 끝)을 고정한 채 zoom을 한 단계 옮긴다. */
  zoomAt(direction: -1 | 1, anchorFraction: number): void;
  /** 가장자리 이동: 그려진 폭의 비율만큼 옮긴다. 누르고 있는 pan이 매끄럽도록 시간 격자에 맞추지 않는다. */
  panByFraction(direction: -1 | 1, viewportFraction: number): void;
  /** 포인터 pan: 절대 시작 위치로 옮기되 타임라인 시간 격자에 맞춘다. */
  panTo(startSeconds: number): void;
  /** 한 시각을 창 안에 둔다. 창을 벗어났으면 그 시각을 중심으로 옮긴다. */
  follow(timeSeconds: number): void;
  /** bar가 사라졌다: 더 그려지는 창이 없으므로 snapshot이 `null`로 돌아간다. */
  detach(): void;
}

type ViewportMedia = { source: object; bounds: MediaTimelineViewportBounds };

// 창 폭은 복사한 값이 아니라 모드로 둔다: `baseline`은 duration이 어디로 가든 기준 폭을 따르고,
// `custom`은 사용자가 zoom한 폭을 초 단위로 유지한다.
type ViewportZoom = { kind: "baseline" } | { kind: "custom"; widthSeconds: number };

const BASELINE_ZOOM: ViewportZoom = { kind: "baseline" };

const WIDTH_EPSILON_SECONDS = MEDIA_TIMELINE_POLICY.durationComparisonEpsilonSeconds;

function hasMediaDuration(durationSeconds: number): boolean {
  return Number.isFinite(durationSeconds) && durationSeconds > 0;
}

function sameSnapshot(
  a: MediaTimelineViewportSnapshot | null,
  b: MediaTimelineViewportSnapshot | null,
): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.startSeconds === b.startSeconds &&
    a.endSeconds === b.endSeconds &&
    a.zoomFactor === b.zoomFactor &&
    a.atBaseline === b.atBaseline &&
    a.canZoomIn === b.canZoomIn &&
    a.canZoomOut === b.canZoomOut
  );
}

class MediaTimelineViewportStore {
  private readonly listeners = new Set<() => void>();
  private owner: object | null = null;
  private media: ViewportMedia | null = null;
  private zoom: ViewportZoom = BASELINE_ZOOM;
  private startSeconds = 0;
  private snapshot: MediaTimelineViewportSnapshot | null = null;
  private warnedSharedOwner = false;

  private readonly baseline: MediaTimelineViewportBaseline;

  constructor(baseline: MediaTimelineViewportBaseline) {
    this.baseline = baseline;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): MediaTimelineViewportSnapshot | null => this.snapshot;

  zoomIn = (): void => this.zoomAt(1, 0.5);

  zoomOut = (): void => this.zoomAt(-1, 0.5);

  resetZoom = (): void => {
    const media = this.media;
    if (!media) return;
    const { baselineSeconds, totalRangeSeconds } = media.bounds;
    const center = this.startSeconds + this.drawnWidth(media) / 2;
    this.zoom = BASELINE_ZOOM;
    this.startSeconds = clampMediaTimelineViewportStart(
      center - baselineSeconds / 2,
      totalRangeSeconds,
      baselineSeconds,
      false,
    );
    this.publish();
  };

  attach(): MediaTimelineViewportSession {
    const token = {};
    if (this.owner !== null && !this.warnedSharedOwner) {
      this.warnedSharedOwner = true;
      devWarn(
        "[c6] MediaTimelineViewportController is attached to more than one MediaTimelineBar; " +
          "the most recently mounted bar owns it. Create one controller per bar.",
      );
    }
    this.owner = token;
    this.media = null;
    this.publish();

    const owns = () => this.owner === token;
    return {
      syncMedia: (media) => {
        if (owns()) this.syncMedia(media);
      },
      zoomAt: (direction, anchorFraction) => {
        if (owns()) this.zoomAt(direction, anchorFraction);
      },
      panByFraction: (direction, viewportFraction) => {
        if (owns()) this.panByFraction(direction, viewportFraction);
      },
      panTo: (startSeconds) => {
        if (owns()) this.panTo(startSeconds);
      },
      follow: (timeSeconds) => {
        if (owns()) this.follow(timeSeconds);
      },
      detach: () => {
        if (!owns()) return;
        this.owner = null;
        this.media = null;
        this.publish();
      },
    };
  }

  private drawnWidth(media: ViewportMedia): number {
    return this.zoom.kind === "baseline"
      ? media.bounds.baselineSeconds
      : clampMediaTimelineViewportWidth(this.zoom.widthSeconds, media.bounds);
  }

  // 기준 폭에 닿은 zoom은 다시 baseline이다 — 우연히 맞닿은 폭을 고정하지 않고 다음 drift를 따른다.
  private normalizeZoom(media: ViewportMedia): void {
    if (
      this.zoom.kind === "custom" &&
      Math.abs(this.drawnWidth(media) - media.bounds.baselineSeconds) <= WIDTH_EPSILON_SECONDS
    ) {
      this.zoom = BASELINE_ZOOM;
    }
  }

  private syncMedia({
    source,
    durationSeconds,
    currentTimeSeconds,
  }: {
    source: object;
    durationSeconds: number;
    currentTimeSeconds: number;
  }): void {
    if (!hasMediaDuration(durationSeconds)) {
      // duration을 잃으면 여는 동작을 다시 준비해, 다시 불러온 source가 새로 시작하게 한다.
      if (this.media === null) return;
      this.media = null;
      this.publish();
      return;
    }

    const bounds = resolveMediaTimelineViewportBounds(durationSeconds, this.baseline);
    if (this.media === null || this.media.source !== source) {
      this.media = { source, bounds };
      this.zoom = BASELINE_ZOOM;
      this.startSeconds = resolveMediaTimelineOpeningStart(bounds, currentTimeSeconds);
      this.publish();
      return;
    }

    // duration drift(늦은 metadata, 바뀐 채널 집합): zoom하지 않은 창은 새 기준 폭을 따르고, 사용자
    // zoom은 초 단위 폭을 유지하며, 시작 위치는 새 범위 안으로 다시 clamp한다.
    const media: ViewportMedia = { source, bounds };
    this.media = media;
    this.normalizeZoom(media);
    this.startSeconds = clampMediaTimelineViewportStart(
      this.startSeconds,
      bounds.totalRangeSeconds,
      this.drawnWidth(media),
      false,
    );
    this.publish();
  }

  private zoomAt(direction: -1 | 1, anchorFraction: number): void {
    const media = this.media;
    if (!media) return;
    const width = this.drawnWidth(media);
    const nextWidth = resolveMediaTimelineZoomedWidth(width, direction, media.bounds);
    if (Math.abs(nextWidth - width) <= WIDTH_EPSILON_SECONDS) return;

    const anchor = Math.max(0, Math.min(1, anchorFraction));
    const anchorTime = this.startSeconds + width * anchor;
    this.zoom = { kind: "custom", widthSeconds: nextWidth };
    this.normalizeZoom(media);
    this.startSeconds = clampMediaTimelineViewportStart(
      anchorTime - anchor * nextWidth,
      media.bounds.totalRangeSeconds,
      nextWidth,
      false,
    );
    this.publish();
  }

  private panByFraction(direction: -1 | 1, viewportFraction: number): void {
    const media = this.media;
    if (!media) return;
    const width = this.drawnWidth(media);
    this.startSeconds = clampMediaTimelineViewportStart(
      this.startSeconds + direction * width * viewportFraction,
      media.bounds.totalRangeSeconds,
      width,
      false,
    );
    this.publish();
  }

  private panTo(startSeconds: number): void {
    const media = this.media;
    if (!media) return;
    this.startSeconds = clampMediaTimelineViewportStart(
      startSeconds,
      media.bounds.totalRangeSeconds,
      this.drawnWidth(media),
    );
    this.publish();
  }

  private follow(timeSeconds: number): void {
    const media = this.media;
    if (!media) return;
    this.startSeconds = resolveMediaTimelineFollowStart(
      timeSeconds,
      this.startSeconds,
      this.drawnWidth(media),
      media.bounds.totalRangeSeconds,
    );
    this.publish();
  }

  // 상태가 snapshot이 되는 유일한 곳. 창이 그대로면 이전 참조를 유지하고 아무도 깨우지 않아, 같은
  // 격자 칸의 pan 프레임이나 반복 sync가 구독자에게 비용을 치르게 하지 않는다.
  private publish(): void {
    const media = this.owner === null ? null : this.media;
    const next =
      media === null
        ? null
        : resolveMediaTimelineViewportView(media.bounds, this.drawnWidth(media), this.startSeconds);
    if (sameSnapshot(this.snapshot, next)) return;
    this.snapshot = next;
    for (const listener of [...this.listeners]) listener();
  }
}

const stores = new WeakMap<MediaTimelineViewportController, MediaTimelineViewportStore>();

/**
 * `MediaTimelineBar`가 그리는 시간 창의 외부 store. zoom·pan·playhead follow는 bar가 소유하고, 이
 * store는 bar가 그리는 창을 알려 옆의 view가 자기 시간축을 맞추게 하며, bar의 zoom 컨트롤과 같은
 * 방식으로 zoom 단계를 옮긴다.
 */
export class MediaTimelineViewportController {
  /** 창이 처음 열리고 100%로 읽히는 기준 폭: 검토 창(`window`) 또는 전체 미디어(`fit`). */
  readonly baseline: MediaTimelineViewportBaseline;
  /** `useSyncExternalStore` 계약. */
  readonly subscribe: (listener: () => void) => () => void;
  /**
   * 그려진 창. 창을 보여 주는 bar가 없으면(붙은 bar가 없거나 아직 duration이 없으면) `null`이다.
   * 필드가 바뀔 때까지 같은 참조다.
   */
  readonly getSnapshot: () => MediaTimelineViewportSnapshot | null;
  /** 창 중심을 기준으로 zoom을 한 단계 들이거나 뺀다. 창이 없거나 갈 수 있는 단계가 없으면 no-op이다. */
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  /** 창 중심을 유지한 채 기준 폭으로 돌아간다. */
  readonly resetZoom: () => void;

  constructor({ baseline = "window" }: MediaTimelineViewportControllerOptions = {}) {
    this.baseline = baseline;
    const store = new MediaTimelineViewportStore(baseline);
    stores.set(this, store);
    this.subscribe = store.subscribe;
    this.getSnapshot = store.getSnapshot;
    this.zoomIn = store.zoomIn;
    this.zoomOut = store.zoomOut;
    this.resetZoom = store.resetZoom;
  }
}

/** @internal bar가 controller를 넘겨받는다. 돌려준 session으로 bar 자신의 입력을 넣는다. */
export function attachMediaTimelineViewport(
  controller: MediaTimelineViewportController,
): MediaTimelineViewportSession {
  const store = stores.get(controller);
  if (!store) throw new Error("[c6] Unknown MediaTimelineViewportController instance.");
  return store.attach();
}
